import React, { useCallback, useEffect, useState } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { adminIntegracaoGet, adminIntegracaoSave, adminApiKeys, adminApiKeyCriar, adminApiKeyRevogar, adminUsers } from '../../lib/api';

const data = (v) => (v ? new Date(v).toLocaleString('pt-BR') : 'nunca');

const IntegracaoSection = () => {
  const [info, setInfo] = useState(null);
  const [limite, setLimite] = useState('');
  const [chaves, setChaves] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [nome, setNome] = useState('');
  const [usuarioId, setUsuarioId] = useState('');
  const [nova, setNova] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const carregar = useCallback(async () => {
    const [i, k, u] = await Promise.all([adminIntegracaoGet(), adminApiKeys(), adminUsers()]);
    setInfo(i); setLimite(String(i.limite_diario)); setChaves(k); setUsuarios(u);
  }, []);
  useEffect(() => { carregar().catch(() => setMsg({ ok: false, text: 'Não foi possível carregar.' })); }, [carregar]);

  const salvarLimite = async (e) => {
    e.preventDefault(); setBusy(true); setMsg(null);
    try { setInfo(await adminIntegracaoSave(Number(limite))); setMsg({ ok: true, text: 'Limite salvo.' }); }
    catch { setMsg({ ok: false, text: 'Informe um número de 0 a 5000.' }); } finally { setBusy(false); }
  };

  const criar = async (e) => {
    e.preventDefault(); setBusy(true); setMsg(null); setNova(null);
    try { const r = await adminApiKeyCriar({ nome, user_id: usuarioId }); setNova(r.chave); setNome(''); await carregar(); }
    catch (err) { setMsg({ ok: false, text: err?.response?.data?.detail || 'Não foi possível criar a chave.' }); } finally { setBusy(false); }
  };

  const revogar = async (k) => {
    if (!window.confirm(`Revogar a chave "${k.nome}"? O sistema que a usa deixa de funcionar na hora.`)) return;
    await adminApiKeyRevogar(k.id); await carregar();
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <section className="bg-white rounded-2xl border border-slate-100 p-6">
        <h2 className="text-lg font-semibold text-slate-900">Limite diário de etiquetas</h2>
        <p className="text-sm text-slate-500 mb-4">Máximo de etiquetas que o sistema de gestão pode emitir por dia. Protege o seu contrato de um erro ou uso indevido. Etiquetas canceladas no dia também contam. Use 0 para não limitar.</p>
        <form onSubmit={salvarLimite} className="flex flex-wrap items-end gap-3">
          <label className="block"><span className="text-sm font-medium text-slate-700">Limite por dia</span><Input className="mt-1 w-32" inputMode="numeric" value={limite} onChange={(e) => setLimite(e.target.value.replace(/\D/g, ''))} required /></label>
          <Button type="submit" disabled={busy} className="inn-btn-primary rounded-full h-10 px-6">Salvar</Button>
          {info && <span className="text-sm text-slate-600 pb-2">Hoje: <strong>{info.usados_hoje}</strong> {info.limite_diario ? `de ${info.limite_diario}` : '(sem limite)'}</span>}
        </form>
      </section>

      <section className="bg-white rounded-2xl border border-slate-100 p-6">
        <h2 className="text-lg font-semibold text-slate-900">Chaves de integração</h2>
        <p className="text-sm text-slate-500 mb-4">Cada sistema conectado usa uma chave própria, que só consegue cotar, emitir, ver a etiqueta e cancelar. Você pode revogar a qualquer momento.</p>
        {chaves.length === 0 ? <p className="text-sm text-slate-500 mb-3">Nenhuma chave criada.</p> : (
          <div className="overflow-x-auto mb-4">
            <table className="w-full text-sm">
              <thead className="text-left text-slate-500 border-b border-slate-100"><tr><th className="py-2">Nome</th><th className="py-2">Usuário</th><th className="py-2">Chave</th><th className="py-2">Último uso</th><th className="py-2" /></tr></thead>
              <tbody>
                {chaves.map((k) => (
                  <tr key={k.id} className="border-b border-slate-50">
                    <td className="py-2">{k.nome}{!k.ativo && <span className="ml-2 text-xs text-red-600">revogada</span>}</td>
                    <td className="py-2 text-slate-600">{k.usuario}</td>
                    <td className="py-2 font-mono text-xs">{k.prefixo}…</td>
                    <td className="py-2 text-slate-600">{data(k.ultimo_uso)}</td>
                    <td className="py-2 text-right">{k.ativo && <button type="button" onClick={() => revogar(k)} className="text-xs font-semibold text-red-600 hover:underline">Revogar</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <form onSubmit={criar} className="flex flex-wrap items-end gap-3 border-t border-slate-100 pt-4">
          <label className="block"><span className="text-sm font-medium text-slate-700">Nome da chave</span><Input className="mt-1 w-56" value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Ex.: Sistema de gestão" required /></label>
          <label className="block"><span className="text-sm font-medium text-slate-700">Usuário que emite</span>
            <select className="mt-1 h-10 rounded-md border border-slate-200 px-2 text-sm" value={usuarioId} onChange={(e) => setUsuarioId(e.target.value)} required>
              <option value="">Escolha...</option>
              {usuarios.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
            </select>
          </label>
          <Button type="submit" disabled={busy} variant="outline" className="rounded-full h-10 px-6">Criar chave</Button>
        </form>
        {nova && (
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm">
            <p className="font-semibold text-amber-900">Guarde esta chave agora. Ela não será mostrada de novo.</p>
            <p className="font-mono text-xs break-all mt-2 select-all">{nova}</p>
            <button type="button" onClick={() => navigator.clipboard?.writeText(nova)} className="mt-2 text-xs font-semibold text-[#2A5BC7] hover:underline">Copiar</button>
          </div>
        )}
      </section>
      {msg && <p className={`text-sm ${msg.ok ? 'text-emerald-600' : 'text-red-600'}`}>{msg.text}</p>}
    </div>
  );
};

export default IntegracaoSection;
